// src/config/pricing.ts
// ─────────────────────────────────────────────────────────────
// ÚNICA FUENTE DE VERDAD DE PRECIOS DE NIIZ.
//
// Regla: ningún módulo puede declarar un precio propio. Todo cobro/abono
// toma su importe de aquí. La unidad base es el Flip Coin:
//     1 coin = $100 COP.
//
// Los precios de planes también existen en `public.plan_prices` (servidor).
// `PLANS` es el espejo para UI; el cobro real lo hace `purchase_plan()`.
// ─────────────────────────────────────────────────────────────

/** Paridad oficial e inmutable. */
export const COIN_COP = 100;

/** Planes: precio mensual en coins y su equivalente COP. */
export const PLANS = {
  FREE: { coins: 0, cop: 0 },
  NARANJA_PLUS: { coins: 500, cop: 50_000 },
  ADVANCED_ACCOUNTING: { coins: 900, cop: 90_000 },
  STUDIO_FIRM: { coins: 1_200, cop: 120_000 },
} as const;

export type PlanKey = keyof typeof PLANS;

/** Catálogo de consumos. `sku` es el identificador que viaja al ledger. */
export const WALLET_PRICES = {
  AI_API_PER_QUERY: 1, // coins (2 si input+output > 10.000 tokens)
  AI_LOCAL_PER_10_QUERIES: 1, // coins
  REPORT_SALES: 1_500, // coins
  REPORT_MARKET_COMPARE: 5_000, // coins
  CERTIFICATE: 150, // coins
  CLOUD_EXTRA_5GB: 5, // coins/mes
  CLOUD_EXTRA_20GB: 18, // coins/mes
  AUCTION_ENTRY: 100, // coins
  MARKETPLACE_DELIVERY: 5, // coins
  DEVICE_EXTRA: 300, // coins
  SECURITY_QUERY: 80, // coins
  SECURITY_MONTHLY: 250, // coins
  TEMPLATE_PREMIUM: 120, // coins
  FISCAL_REPORT: 50, // coins
  VELAS_DAILY: 1_200, // coins
} as const;

export type WalletSku = keyof typeof WALLET_PRICES;

/** Línea B2B: se factura en COP directo, no con wallet. */
export const B2B = {
  VELAS_DAILY_COP: 120_000,
  VELAS_MONTHLY_COP: 5_000_000,
  VELAS_ANNUAL_COP: 50_000_000, // 10 meses pagados + 2 gratis
} as const;

/** Paquetes de recarga. Fuera de estos montos: floor(cop/100) sin bono. */
export const RECHARGE_BONUS = [
  { cop: 10_000, baseCoins: 75, bonusCoins: 3 },
  { cop: 20_000, baseCoins: 150, bonusCoins: 7 },
  { cop: 30_000, baseCoins: 230, bonusCoins: 10 },
  { cop: 50_000, baseCoins: 390, bonusCoins: 14 },
  { cop: 80_000, baseCoins: 630, bonusCoins: 21 },
] as const;

/** Recarga mínima permitida. */
export const MIN_RECHARGE_COP = 10_000;

export interface RechargeQuote {
  cop: number;
  baseCoins: number;
  bonusCoins: number;
  totalCoins: number;
  isPackage: boolean;
}

/**
 * Cotiza una recarga. Única función autorizada para convertir COP → coins.
 * - Paquete exacto: usa la tabla RECHARGE_BONUS.
 * - Cualquier otro monto ≥ mínimo: floor(cop / COIN_COP), sin bono.
 * - Por debajo del mínimo o inválido: 0 coins.
 */
export const quoteRecharge = (copAmount: number): RechargeQuote => {
  const cop = Math.floor(Number(copAmount) || 0);
  if (cop < MIN_RECHARGE_COP) {
    return { cop, baseCoins: 0, bonusCoins: 0, totalCoins: 0, isPackage: false };
  }
  const pkg = RECHARGE_BONUS.find((p) => p.cop === cop);
  if (pkg) {
    return {
      cop,
      baseCoins: pkg.baseCoins,
      bonusCoins: pkg.bonusCoins,
      totalCoins: pkg.baseCoins + pkg.bonusCoins,
      isPackage: true,
    };
  }
  const baseCoins = Math.floor(cop / COIN_COP);
  return { cop, baseCoins, bonusCoins: 0, totalCoins: baseCoins, isPackage: false };
};

/** Etiqueta uniforme: "150 coins (≈ $15.000 COP)". */
export const formatCoins = (coins: number): string => {
  const n = Math.max(0, Math.round(Number(coins) || 0));
  return `${n.toLocaleString('es-CO')} coins (≈ $${(n * COIN_COP).toLocaleString('es-CO')} COP)`;
};

/** Etiqueta corta para tablas y badges. */
export const formatCoinsShort = (coins: number): string =>
  `${Math.max(0, Math.round(Number(coins) || 0)).toLocaleString('es-CO')} coins`;
